"use client";

import type { ReactNode } from "react";
import RefBackground from "./RefBackground";
import { RefGridContext } from "./RefGridContext";
import SectionDecorations from "./SectionDecorations";

/**
 * Scope wrapper for the reference section library. Establishes the Onest
 * typeface and ink text color confined to this subtree via an isolated
 * stacking context. Two surfaces:
 *
 * - default: opaque warm-white band + atmospheric blob background (feature
 *   pages). The body grid is intentionally covered.
 * - `onGrid`: transparent on md+ — sections sit directly on the page-wide
 *   hairline body grid (homepage). No blobs: `GridClearing` paints opaque
 *   page-colored rects, which would punch visible rectangles into any
 *   backdrop that isn't the plain page color. Below md the grid isn't
 *   followed (see CLAUDE.md "Mobile") and clearings render nothing, so the
 *   band paints the plain page color to keep text off the raw grid lines.
 */
export default function RefSections({
  children,
  onGrid = false,
}: {
  children: ReactNode;
  onGrid?: boolean;
}) {
  return (
    <RefGridContext.Provider value={onGrid}>
      <div
        className={`relative isolate font-onest text-ink antialiased ${
          onGrid ? "bg-[#f8fafc] md:bg-transparent" : "bg-bg-warm"
        }`}
      >
        {!onGrid && <RefBackground />}
        {onGrid && <SectionDecorations />}
        {children}
      </div>
    </RefGridContext.Provider>
  );
}
